import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination, EmptyState, useToast } from '@centraoneplus/ui';
import { Building2, X } from 'lucide-react';
import { organizationService, type Organization } from '@services/customer.service';
import { useBreakpoint } from '@shared/hooks/useBreakpoint';
import { OrgRegisterHead, OrgRegisterRow } from './OrgRegister';
import { ORG_SORTS, ORG_SORT_IDS, toOrgRow, type OrgRow, type OrgSort } from './orgTypes';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ThemedSelect } from '@components/common/ThemedSelect';

// 26b — the organisation register, in the artboard's floating-card shell.
//
// ── What the artboard asked for, and what the backend can answer ────────────
//
// 26b draws PLAN · SEATS · OPEN · BREACHED · RENEWAL · LAST CONTACT. None of
// those exist. `organizations` is name/domain/website/industry/size/tags/
// metadata/customFields/externalId/createdAt/updatedAt — no tier, no status,
// no hierarchy, no ticket counts — and the list endpoint returns exactly one
// aggregate, `_count.customers`. The artboard's own footer flagged this
// ("pending schema confirmation"), so the layout ships over the real columns
// and `OrgRegister.tsx` documents the substitution column by column.
//
// ── Two live bugs this rebuild fixes, because they are the same code ────────
//
// 1. THE FILTERS EMPTIED THE REGISTER. The old page sent `status`, `tier` and
//    `size`. The BFF binds this route as `@Query() dto: OrganizationFilterDto`
//    under `ValidationPipe({ whitelist: true, forbidNonWhitelisted: true })`,
//    and that DTO accepts only search/industry/page/limit/sortBy/sortOrder —
//    so any of those three turned the request into a 400, the catch below
//    turned the 400 into `[]`, and using a filter looked like "no matching
//    organizations". Only whitelisted params are sent now.
//
// 2. PAGINATED SUBSIDIARIES VANISHED. The old page rendered
//    `filter(o => !o.parentId)` to find tree roots. `parentId` is not a column
//    on this table at all, so the predicate happened to pass everything — but
//    the moment anything set it, every row whose parent sat on another page
//    disappeared from the register. The hierarchy is gone with the field that
//    never backed it.
//
// `sortBy` is whitelisted in `orgTypes.ts` for a third reason: customer-service
// builds `orderBy: { [sortBy]: sortOrder }` with no validation, so an unknown
// field is a 500 rather than a 400.

/** The floating shell: panels sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const PANEL = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

/** 26b's control: 34px, radius 8, accent on hover. */
const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-selected)]';
/** The same control, with the header card's 10px corners. */
const HEAD_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');

/** The industry list the page has always offered. Server-side, whitelisted. */
const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Retail', 'Manufacturing', 'Education'];

const PAGE_SIZE = 20;

/**
 * The register's cohorts.
 *
 * The artboard's AT RISK / RENEWING / QUIET need renewal dates and breach
 * counts, so they cannot be computed. LINKED / UNLINKED can: it is the one
 * condition `_count.customers` decides, and it is the condition that matters
 * most here — an organisation no customer points at is invisible to tickets,
 * because every ticket routes through a customer.
 */
type Cohort = 'all' | 'linked' | 'unlinked';
const COHORTS: { id: Cohort; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'linked', label: 'Linked' },
  { id: 'unlinked', label: 'Unlinked' },
];

function KpiCard({
  value,
  label,
  note,
  tone,
}: {
  value: string;
  label: string;
  note: string;
  tone?: 'ink' | 'terracotta';
}) {
  return (
    <div className={`min-w-0 flex-1 ${PANEL} px-[18px] py-4`} style={CARD}>
      <div
        className="font-mono text-[26px] font-semibold leading-none tabular-nums"
        style={{ color: tone === 'terracotta' ? 'var(--co-terracotta)' : 'var(--co-ink)' }}
      >
        {value}
      </div>
      <div className="mt-2 font-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        {label}
      </div>
      <div className="mt-2 border-t border-[var(--co-line-subtle)] pt-[9px] text-[12px] leading-[1.45] text-[var(--co-ink-2)]">
        {note}
      </div>
    </div>
  );
}

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const tier = useBreakpoint();

  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [sortBy, setSortBy] = useState<OrgSort>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortOpen, setSortOpen] = useState(false);
  const [cohort, setCohort] = useState<Cohort>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState<string[]>([]);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  // Typing must not fire a request per keystroke; the old page filtered the
  // loaded page instead, which silently searched only 20 of N records.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchOrganizations = useCallback(async () => {
    setIsLoading(true);
    try {
      // ONLY whitelisted params. See the header note — anything else is a 400
      // that this catch would turn into an empty register.
      const response = await organizationService.getOrganizations({
        search: debouncedSearch || undefined,
        industry: industry || undefined,
        page: currentPage,
        limit: PAGE_SIZE,
        sortBy,
        sortOrder,
      });
      setOrganizations(response.data ?? []);
      setTotal(response.total ?? 0);
      setTotalPages(response.totalPages ?? 1);
      setFailed(false);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      setOrganizations([]);
      setTotal(0);
      setTotalPages(1);
      // The old page could not tell "no organisations" from "the request
      // failed" — both rendered the same empty state, which is how bug 1 above
      // stayed invisible.
      setFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, industry, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // A narrowed list can leave you past the last page.
  useEffect(() => {
    setCurrentPage(1);
    setSelected([]);
  }, [debouncedSearch, industry, sortBy, sortOrder]);

  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuFor(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuFor]);

  useEffect(() => {
    if (!sortOpen) return;
    const close = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [sortOpen]);

  const rows: OrgRow[] = useMemo(() => organizations.map(toOrgRow), [organizations]);

  const visible = useMemo(
    () =>
      cohort === 'all'
        ? rows
        : rows.filter((r) => (cohort === 'unlinked' ? r.people === 0 : r.people > 0)),
    [rows, cohort],
  );

  // Every figure below is over the rows actually loaded. When the whole
  // register fits one page that is the whole register, and the foot band says
  // which of the two it is rather than leaving the reader to assume.
  const page = useMemo(() => {
    const people = rows.reduce((sum, r) => sum + r.people, 0);
    return {
      people,
      unlinked: rows.filter((r) => r.people === 0).length,
      linked: rows.filter((r) => r.people > 0).length,
      noDomain: rows.filter((r) => !r.domain).length,
    };
  }, [rows]);

  const wholeRegister = total <= PAGE_SIZE && !debouncedSearch && !industry;
  const allOnPageSelected = visible.length > 0 && visible.every((r) => selected.includes(r.id));
  const hasNarrowing = Boolean(debouncedSearch || industry || cohort !== 'all');

  const clearAll = () => {
    setSearch('');
    setIndustry('');
    setCohort('all');
  };

  const copyDomains = async () => {
    const domains = rows
      .filter((r) => selected.includes(r.id))
      .map((r) => r.domain)
      .filter((d): d is string => Boolean(d));
    if (domains.length === 0) {
      toast.error('None of the selected organisations has a domain');
      return;
    }
    try {
      await navigator.clipboard.writeText(domains.join(', '));
      toast.success(`Copied ${domains.length} domain${domains.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Could not copy to the clipboard');
    }
  };

  const sortLabel = ORG_SORTS.find((s) => s.id === sortBy)?.label ?? 'Date added';

  return (
    <div
      className="co-orgs flex h-full min-h-0 flex-col gap-[14px] overflow-y-auto bg-[var(--co-canvas)] py-4 pr-4"
      data-tier={tier}
    >
      {/* ── Head card: title, subtitle, controls, cohorts, sort ───────────── */}
      <div className={`flex-none ${PANEL} px-5 py-[18px]`} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + subtitle on the left, the utility cluster on the right. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.08] tracking-[-.018em] text-[var(--co-ink)]">
              Organizations
            </h1>
            <div className="mt-[6px] font-mono text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-2)]">
              {isLoading && organizations.length === 0
                ? 'Reading the register…'
                : `${total} organisation${total === 1 ? '' : 's'} · ${page.people} ${
                    page.people === 1 ? 'person' : 'people'
                  } on record${wholeRegister ? '' : ' on this page'}`}
            </div>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: every other header control, wrapping when space runs out. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
            <div className="relative min-w-0 flex-none" style={{ width: 260, maxWidth: '100%' }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--co-ink-3)"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
                className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or domain"
                aria-label="Search organisations by name or domain"
                className="h-[34px] w-full rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] pl-[31px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* The artboard's "All segments" slot. Industry is the one
                narrowing filter this endpoint actually accepts. */}
            <label className={HEAD_CONTROL} style={industry ? { borderColor: 'var(--co-brass)' } : undefined}>
              <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Industry
              </span>
              <ThemedSelect
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                aria-label="Filter by industry"
                className="cursor-pointer border-0 bg-transparent pr-1 text-[12.5px] text-[var(--co-ink-2)] focus:outline-none"
              >
                <option value="">All</option>
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </ThemedSelect>
            </label>

            {hasNarrowing && (
              <button type="button" onClick={clearAll} className={HEAD_CONTROL}>
                Clear
              </button>
            )}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-[6px] rounded-[10px] bg-[var(--co-sunken)] p-1">
            {COHORTS.map((c) => {
              const on = c.id === cohort;
              const count =
                c.id === 'all' ? rows.length : c.id === 'linked' ? page.linked : page.unlinked;
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setCohort(c.id)}
                  className="flex items-center gap-[6px] rounded-lg px-3 py-[7px] font-mono text-[10.5px] uppercase tracking-[.12em]"
                  style={{
                    background: on ? 'var(--co-raised)' : 'transparent',
                    boxShadow: on ? 'var(--co-shadow)' : undefined,
                    color: on ? 'var(--co-ink)' : 'var(--co-ink-2)',
                    fontWeight: on ? 600 : 500,
                  }}
                >
                  {c.label}
                  <span
                    className="font-semibold"
                    style={{ color: on ? 'var(--co-brass)' : 'var(--co-ink-3)' }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative" ref={sortRef}>
            <button
              type="button"
              onClick={() => setSortOpen((v) => !v)}
              aria-expanded={sortOpen}
              className={HEAD_CONTROL}
            >
              <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Sort
              </span>
              {sortLabel}
              <span className="text-[var(--co-ink-3)]" aria-hidden="true">
                ▾
              </span>
            </button>
            {sortOpen && (
              <ul
                role="listbox"
                className={`absolute right-0 top-[38px] z-20 w-[210px] overflow-hidden rounded-lg ${PANEL}`}
                style={{ boxShadow: 'var(--co-shadow-pop)' }}
              >
                {ORG_SORTS.map((s) => {
                  const on = s.id === sortBy;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={on}
                        onClick={() => {
                          if (ORG_SORT_IDS.has(s.id)) setSortBy(s.id);
                          setSortOpen(false);
                        }}
                        className="flex h-[32px] w-full items-center px-3 text-left text-[12.5px] text-[var(--co-ink)] hover:bg-[var(--co-selected)]"
                        style={{
                          fontWeight: on ? 600 : 400,
                          background: on ? 'var(--co-selected)' : undefined,
                        }}
                      >
                        {s.label}
                      </button>
                    </li>
                  );
                })}
                <li className="border-t border-[var(--co-line-subtle)]">
                  <button
                    type="button"
                    onClick={() => {
                      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
                      setSortOpen(false);
                    }}
                    className="flex h-[32px] w-full items-center px-3 text-left font-mono text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)] hover:bg-[var(--co-selected)]"
                  >
                    {sortBy === 'createdAt'
                      ? sortOrder === 'asc'
                        ? 'Newest first'
                        : 'Oldest first'
                      : sortOrder === 'asc'
                        ? 'Z → A'
                        : 'A → Z'}
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── The four figures ──────────────────────────────────────────────── */}
      <div className="flex flex-none flex-wrap gap-[14px]">
        <KpiCard
          value={String(total)}
          label="Organisations"
          note="Every account on record for this tenant."
        />
        <KpiCard
          value={String(page.people)}
          label="People on record"
          note={
            wholeRegister
              ? 'Customer records pointing at an account.'
              : `Across the ${rows.length} on this page.`
          }
        />
        <KpiCard
          value={String(page.unlinked)}
          label="Unlinked"
          tone={page.unlinked > 0 ? 'terracotta' : 'ink'}
          note="No customer record points here, so no ticket can reach them."
        />
        <KpiCard
          value={String(page.noDomain)}
          label="Without a domain"
          tone={page.noDomain > 0 ? 'terracotta' : 'ink'}
          note="Domain is how an inbound address is matched to an account."
        />
      </div>

      {/* ── The register ──────────────────────────────────────────────────── */}
      {/* The floor keeps the empty state clear of the foot band on a short
          viewport; the page scrolls instead of the register collapsing. */}
      <section className={`flex min-h-[460px] flex-1 flex-col overflow-hidden ${PANEL}`} style={CARD}>
        {selected.length > 0 && (
          <div className="flex flex-none items-center gap-3 border-b border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-[10px]">
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[.12em] text-[var(--co-ink)]">
              {`${selected.length} selected`}
            </span>
            <div className="flex-1" />
            <button type="button" onClick={copyDomains} className={CONTROL}>
              Copy domains
            </button>
            <button type="button" onClick={() => setSelected([])} className={CONTROL}>
              Clear
            </button>
          </div>
        )}

        <OrgRegisterHead
          tier={tier}
          allSelected={allOnPageSelected}
          onSelectAllOnPage={(checked) =>
            setSelected(checked ? visible.map((r) => r.id) : [])
          }
          sortBy={sortBy}
          sortOrder={sortOrder}
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading && organizations.length === 0 ? (
            <p className="px-5 py-10 text-[13px] text-[var(--co-ink-3)]">Reading the register…</p>
          ) : failed ? (
            <p className="max-w-[92ch] px-5 py-10 text-[13px] leading-[1.6] text-[var(--co-ink-3)]">
              The organisation register could not be read. This is a failed request rather than an
              empty tenant — the rows may well be there.
            </p>
          ) : visible.length === 0 ? (
            <div className="py-6">
              <EmptyState
                icon={<Building2 className="h-12 w-12" />}
                title={hasNarrowing ? 'Nothing matches' : 'No organisations yet'}
                description={
                  hasNarrowing
                    ? 'No organisation on this page matches the current search, industry or cohort.'
                    : 'Organisations are created by import or by the API. Nothing in the ticket path creates one automatically, so this stays empty until something writes to it.'
                }
                action={
                  hasNarrowing ? (
                    <button type="button" onClick={clearAll} className={CONTROL}>
                      Clear
                    </button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            visible.map((row) => (
              // The ref goes on the WRAPPER, not on the menu: it has to
              // contain the ⋯ trigger too. With the menu alone, mousedown
              // (which precedes click) would close the menu and the trigger's
              // own click would immediately reopen it — a button that can
              // never be used to close what it opened.
              <div
                key={row.id}
                className="relative"
                ref={menuFor === row.id ? menuRef : undefined}
              >
                <OrgRegisterRow
                  row={row}
                  tier={tier}
                  selected={selected.includes(row.id)}
                  onToggle={(checked) =>
                    setSelected((prev) =>
                      checked ? [...prev, row.id] : prev.filter((id) => id !== row.id),
                    )
                  }
                  onOpen={() => navigate(`/organizations/${row.id}`)}
                  onMenu={() => setMenuFor((id) => (id === row.id ? null : row.id))}
                />
                {menuFor === row.id && (
                  <div
                    className={`absolute right-5 top-[44px] z-20 w-[190px] overflow-hidden rounded-lg ${PANEL}`}
                    style={{ boxShadow: 'var(--co-shadow-pop)' }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setMenuFor(null);
                        navigate(`/organizations/${row.id}`);
                      }}
                      className="flex h-[32px] w-full items-center px-3 text-left text-[12.5px] text-[var(--co-ink)] hover:bg-[var(--co-selected)]"
                    >
                      View organisation
                    </button>
                    <button
                      type="button"
                      disabled={!row.domain}
                      onClick={async () => {
                        setMenuFor(null);
                        if (!row.domain) return;
                        try {
                          await navigator.clipboard.writeText(row.domain);
                          toast.success('Domain copied');
                        } catch {
                          toast.error('Could not copy to the clipboard');
                        }
                      }}
                      className="flex h-[32px] w-full items-center px-3 text-left text-[12.5px] text-[var(--co-ink)] hover:bg-[var(--co-selected)] disabled:cursor-not-allowed disabled:text-[var(--co-ink-4)]"
                    >
                      {row.domain ? 'Copy domain' : 'No domain'}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* The foot band. Its job is to say what the figures above cover. */}
        <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--co-line)] bg-[var(--co-sunken)] px-5 py-3">
          <span className="font-mono text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
            {wholeRegister && cohort === 'all'
              ? `All ${total} organisation${total === 1 ? '' : 's'} shown`
              : `Showing ${visible.length} of ${total}`}
          </span>
          <div className="flex-1" />
          <span className="text-[12px] leading-[1.45] text-[var(--co-ink-2)]">
            {wholeRegister
              ? 'Sorted by ' +
                sortLabel.toLowerCase() +
                '. Plan, seats, renewal and per-account ticket counts are not recorded for organisations.'
              : 'The four figures above describe this page, not the whole register.'}
          </span>
        </div>
      </section>

      {totalPages > 1 && (
        <div className={`flex-none ${PANEL} px-5 py-3`} style={CARD}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => {
              setCurrentPage(p);
              setSelected([]);
            }}
          />
        </div>
      )}
    </div>
  );
}
